import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { rememberPassword, recallPassword, forgetPassword } from "./albumPassword";
import "./MediaAlbum.css";

const LOGO_URL =
  "https://nbetrdzkoskuwnpbvxvh.supabase.co/storage/v1/object/public/public-assets/keepsake-logo-koala.png";

function MediaAlbum() {
  const { slug } = useParams();
  const [plaque, setPlaque] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const FUNCTIONS_BASE =
    process.env.REACT_APP_SUPABASE_FUNCTIONS_URL ||
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;

  const [password, setPassword] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [resetState, setResetState] = useState(null); // null | "sending" | { message }
  const justReset = useLocation().state?.passwordReset === true; // arrived from the reset page

  const fetchOrderData = async (pwOverride) => {
    try {
      setLoading(true);
      setError(null);

      const currentPw = pwOverride ?? password;

      if (!currentPw || currentPw.length < 3) {
        setError("Please enter your album password.");
        return;
      }

      const resp = await fetch(`${FUNCTIONS_BASE}/album_get_media`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ slug, password: currentPw }),
      });

      if (!resp.ok) {
        const errJson = await resp.json().catch(() => null);
        console.error("album_get_media error response:", resp.status, errJson);

        if (resp.status === 401 || resp.status === 429) {
          forgetPassword(slug);
          throw new Error(
            errJson?.error || "Invalid password. Please try again."
          );
        }

        const msg = errJson?.error || "Failed to load album.";
        throw new Error(msg);
      }

      const payload = await resp.json();
      const itemsFromFunction = payload.items || [];

      setIsAuthed(true);
      setPlaque(payload.plaque || null);
      setMediaItems(itemsFromFunction);
      rememberPassword(slug, currentPw);
    } catch (err) {
      console.error("Error fetching order:", err);
      setError(err.message || "Failed to load album.");
      setIsAuthed(false);
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  // "Forgot password?": emails a one-time reset link to the address the plaque was ordered with.
  const requestReset = async () => {
    setResetState("sending");
    try {
      const resp = await fetch(`${FUNCTIONS_BASE}/album_password_reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ action: "request", slug }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.sent) {
        setResetState({ message: `We've emailed a reset link to ${data.email}. It works once and expires in 1 hour. If it doesn't arrive, check your spam folder.` });
      } else if (data.error === "too_many_emails") {
        setResetState({ message: `We've already sent several reset emails to ${data.email} today. Please use the most recent one, or try again tomorrow.` });
      } else if (data.error === "no_email") {
        setResetState({ message: `We don't have an email address for this album. Please contact ${data.contact || "keepsakeqr@gmail.com"} and we'll help you get back in.` });
      } else {
        throw new Error(data.error || "Request failed");
      }
    } catch (err) {
      console.error("album_password_reset error:", err);
      setResetState({ message: "Sorry, we couldn't send a reset email just now. Please try again in a few minutes." });
    }
  };

  useEffect(() => {
    setIsAuthed(false);
    setMediaItems([]);
    setError(null);
    setResetState(null);

    const cachedPw = recallPassword(slug);
    if (cachedPw) {
      setPassword(cachedPw);
      fetchOrderData(cachedPw);
    } else {
      setPassword("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const openMedia = (index) => setSelectedIndex(index);
  const closeMedia = () => setSelectedIndex(null);

  const goNext = useCallback(() => {
    setSelectedIndex((i) => (i < mediaItems.length - 1 ? i + 1 : i));
  }, [mediaItems.length]);

  const goPrev = useCallback(() => {
    setSelectedIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  // Keyboard navigation for modal
  useEffect(() => {
    if (selectedIndex === null) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") closeMedia();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedIndex, goNext, goPrev]);

  // Touch swipe for modal
  useEffect(() => {
    if (selectedIndex === null) return;
    let startX = 0;
    const onStart = (e) => { startX = e.touches[0].clientX; };
    const onEnd = (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goPrev();
      }
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
    };
  }, [selectedIndex, goNext, goPrev]);

  const getMediaUrl = (item) => item.url || item.signedUrl || null;
  // Thumbnail = Image Transformations URL (Pro plan). If it fails to load, fall back to the full image.
  const getThumbUrl = (item) => item.thumbUrl || getMediaUrl(item);

  // Signed URLs contain the object path, so the extension is visible even without file_path.
  const pathOf = (item) => {
    const u = item.file_path || getMediaUrl(item) || "";
    return u.split("?")[0].toLowerCase();
  };
  const isImageItem = (item) => /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif|avif)$/.test(pathOf(item));
  const isVideoItem = (item) => /\.(mp4|webm|ogg|mov|avi|m4v)$/.test(pathOf(item));

  const selectedMedia = selectedIndex !== null ? mediaItems[selectedIndex] : null;

  return (
    <div
      className={`album-container ${!isAuthed ? "album-container--locked" : ""}`}
    >
      {/* Password gate */}
      {!isAuthed && (
        <div className="password-gate">
          <div className="password-card">
            <div className="password-logo">
              <img src={LOGO_URL} alt="KeepsakeQR" />
            </div>
            <h1 className="password-title">Your Keepsake Memories</h1>
            <p className="password-subtitle">
              Enter your password to unlock this album
            </p>
            <div className="password-input-group">
              <input
                id="album-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter album password"
                onKeyDown={(e) => e.key === "Enter" && fetchOrderData()}
                autoFocus
              />
              <button
                className="password-submit"
                onClick={() => fetchOrderData()}
                disabled={loading}
              >
                {loading ? (
                  <span className="btn-spinner"></span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                )}
              </button>
            </div>
            {justReset && !error && (
              <p className="password-success">Your password has been changed. Enter it above to open your album.</p>
            )}
            {error && <p className="password-error">{error}</p>}
            {resetState && resetState !== "sending" ? (
              <p className="password-notice">{resetState.message}</p>
            ) : (
              <button
                type="button"
                className="password-forgot"
                onClick={requestReset}
                disabled={resetState === "sending"}
              >
                {resetState === "sending" ? "Sending…" : "Forgot password?"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Album view */}
      {isAuthed && (
        <>
          <div className="album-header">
            <img src={LOGO_URL} alt="KeepsakeQR" className="album-header-logo" />
            <div>
              <h1 className="album-title">{plaque?.text1 || "Your Memories"}</h1>
              {mediaItems.length > 0 && (
                <p className="album-count">
                  {mediaItems.length} {mediaItems.length === 1 ? "item" : "items"}
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading your memories...</p>
            </div>
          ) : mediaItems.length === 0 ? (
            <div className="empty-album">
              <p>This album is empty</p>
            </div>
          ) : (
            <div className="media-grid">
              {mediaItems.map((item, index) => {
                const mediaUrl = getMediaUrl(item);
                if (!mediaUrl) return null;

                const image = isImageItem(item);
                const video = isVideoItem(item);

                return (
                  <div
                    key={item.id || index}
                    className="media-item"
                    onClick={() => openMedia(index)}
                  >
                    {image ? (
                      <img
                        src={getThumbUrl(item)}
                        alt={`Memory ${index + 1}`}
                        loading="lazy"
                        onError={(e) => {
                          // thumbnail unavailable (no Image Transformations on this plan) → full image
                          if (e.target.src !== mediaUrl && mediaUrl) {
                            e.target.src = mediaUrl;
                            return;
                          }
                          e.target.style.display = "none";
                          if (e.target.nextSibling)
                            e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : video ? (
                      <div className="video-thumbnail">
                        <video src={mediaUrl} muted preload="metadata" />
                        <div className="play-overlay">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <div className="media-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                        </svg>
                      </div>
                    )}
                    <div className="media-placeholder" style={{ display: "none" }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Fullscreen modal with navigation */}
      {selectedMedia && (
        <div className="media-modal" onClick={closeMedia}>
          <div className="modal-top-bar">
            <span className="modal-counter">
              {selectedIndex + 1} / {mediaItems.length}
            </span>
            <button className="modal-close" onClick={closeMedia}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {selectedIndex > 0 && (
            <button
              className="modal-nav modal-nav--prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}

          {selectedIndex < mediaItems.length - 1 && (
            <button
              className="modal-nav modal-nav--next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const mediaUrl = getMediaUrl(selectedMedia);
              if (!mediaUrl) return null;

              if (isImageItem(selectedMedia)) {
                return (
                  <img src={mediaUrl} alt="Media" className="modal-media" />
                );
              } else if (isVideoItem(selectedMedia)) {
                return (
                  <video
                    src={mediaUrl}
                    controls
                    autoPlay
                    className="modal-media"
                  />
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaAlbum;
